/**
 * Zod input schemas: resources. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { z } from 'zod';
import { TOOL_SLUGS } from '../tools.js';
import { Iso3166CodeSchema } from './primitives.js';
const ResourceScopeSchema = z.enum(['global', 'countries', 'regions']);
export const ResourceAdminCreateSchema = z
    .object({
    name: z.string().trim().min(2).max(200),
    releaseDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    scope: ResourceScopeSchema,
    countryCodes: z.array(Iso3166CodeSchema).max(50),
    regionGroups: z.array(z.string().max(50)).max(20),
    /**
     * Tools this resource is scoped to. Empty array = no tool restriction.
     * Non-empty = only users with access to at least one listed tool see it.
     */
    toolSlugs: z.array(z.enum(TOOL_SLUGS)).max(10).optional().default([]),
    /** Pre-uploaded Storage path of the PDF. */
    fileStoragePath: z.string().min(1),
    fileMimeType: z.literal('application/pdf'),
    fileName: z.string().min(1).max(255),
    fileSizeBytes: z.number().int().positive().max(50 * 1024 * 1024), // 50 MB
})
    .superRefine((data, ctx) => {
    if (data.scope === 'countries' && data.countryCodes.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['countryCodes'],
            message: 'Pick at least one country',
        });
    }
    if (data.scope === 'regions' && data.regionGroups.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['regionGroups'],
            message: 'Pick at least one region',
        });
    }
});
export const ResourceAdminUpdateSchema = z
    .object({
    resourceId: z.string().min(1),
    name: z.string().trim().min(2).max(200),
    releaseDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    scope: ResourceScopeSchema,
    countryCodes: z.array(Iso3166CodeSchema).max(50),
    regionGroups: z.array(z.string().max(50)).max(20),
    toolSlugs: z.array(z.enum(TOOL_SLUGS)).max(10).optional().default([]),
})
    .superRefine((data, ctx) => {
    if (data.scope === 'countries' && data.countryCodes.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['countryCodes'],
            message: 'Pick at least one country',
        });
    }
    if (data.scope === 'regions' && data.regionGroups.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['regionGroups'],
            message: 'Pick at least one region',
        });
    }
});
export const ResourceAdminDeleteSchema = z.object({
    resourceId: z.string().min(1),
});
//# sourceMappingURL=resources.js.map